import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { LogoMark } from "@/components/Logo";

const NODES = [
  { code: "US", label: "MidHq · US desk", x: "6%", y: "22%", z: 90, c: "#3B82F6" },
  { code: "MX", label: "Mexico", x: "4%", y: "70%", z: 40, c: "#38BDF8" },
  { code: "BG", label: "Bulgaria", x: "74%", y: "16%", z: 70, c: "#F59E0B" },
  { code: "NL", label: "Amsterdam", x: "78%", y: "66%", z: 110, c: "#F59E0B" },
];

export function CorridorOrb() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [14, -14]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-18, 18]), { stiffness: 60, damping: 18 });

  useEffect(() => {
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth) * 2 - 1);
      my.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [mx, my]);

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[560px]" style={{ perspective: 1200 }} data-testid="hero-corridor-orb">
      <motion.div className="absolute inset-0" style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
        <div className="absolute inset-[6%] rounded-full border border-sky-400/30 ring-a" style={{ transformStyle: "preserve-3d" }}>
          <span className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-400 shadow-[0_0_20px_4px_rgba(56,189,248,0.7)]" />
        </div>
        <div className="absolute inset-[14%] rounded-full border border-amber-400/30 ring-b">
          <span className="absolute bottom-0 left-1/2 h-2.5 w-2.5 -translate-x-1/2 translate-y-1/2 rounded-full bg-amber-400 shadow-[0_0_18px_4px_rgba(245,158,11,0.7)]" />
        </div>
        <div className="absolute inset-[22%] rounded-full border border-dashed border-white/20 ring-c" />
        <div className="absolute inset-[24%] rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.5),transparent_65%)] core-pulse" />
        <div className="absolute inset-[38%] grid place-items-center rounded-full border border-white/15 bg-[#0b1322]" style={{ transform: "translateZ(60px)" }}>
          <LogoMark size={64} />
        </div>
        {NODES.map((n, i) => (
          <motion.div key={n.code} className="absolute" style={{ left: n.x, top: n.y, transform: `translateZ(${n.z}px)` }}
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1 + i * 0.15, duration: 0.7 }}>
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-[#0b111c]/85 px-3 py-1.5 backdrop-blur-md">
              <span className="h-2 w-2 rounded-full" style={{ background: n.c, boxShadow: `0 0 12px ${n.c}` }} />
              <span className="font-mono text-[11px] text-white">{n.code}</span>
              <span className="hidden text-[11px] text-slate-300 sm:inline">{n.label}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
